"use client";

import Link from "next/link";
import { ChevronIcon, FolderIcon } from "@/components/icons";
import { FolderEditInput } from "./FolderEditInput";
import { subtreeAttentionCounts } from "./folderTreeModel";
import type { FolderNode, SidebarWorker } from "./types";
import type { FolderEditor } from "./useFolderEditing";
import type { FolderExpansion } from "./useFolderExpansion";
import type { WorkerMovement } from "./useWorkerMoving";
import { AttentionIndicator } from "./AttentionIndicator";
import { WorkerRow } from "./WorkerRow";

/** What every row of the tree shares, handed down the recursion as one object. */
export interface FolderTreeScope {
  pathname: string;
  onCloseDrawer: () => void;
  workersByFolder: Map<string, SidebarWorker[]>;
  expansion: FolderExpansion;
  editor: FolderEditor;
  movement: WorkerMovement;
}

/** A department row with its actions, then its sub-departments and workers. */
export function FolderRow({
  node,
  depth,
  scope,
}: {
  node: FolderNode;
  depth: number;
  scope: FolderTreeScope;
}) {
  const { pathname, onCloseDrawer, workersByFolder, expansion, editor, movement } = scope;
  const open = expansion.isExpanded(node.id);
  const isRenaming = editor.editing?.mode === "rename" && editor.editing.id === node.id;
  const activeDash = pathname === `/folders/${node.id}`;
  const folderWorkers = workersByFolder.get(node.id) ?? [];
  const collapsedCounts = open ? null : subtreeAttentionCounts(node, workersByFolder);

  return (
    <div>
      {isRenaming ? (
        <FolderEditInput
          depth={depth}
          value={editor.editValue}
          onChange={editor.setEditValue}
          onCommit={editor.commit}
          onCancel={editor.cancel}
        />
      ) : (
        <div
          {...movement.folderProps(node.id)}
          data-folder-drop={node.id}
          style={{ paddingLeft: `${4 + depth * 14}px` }}
          className={`group data-[drop-active=true]:bg-violet-500/20 data-[drop-active=true]:ring-1 data-[drop-active=true]:ring-violet-400 flex items-center gap-1 rounded-lg pr-1 text-sm transition ${
            activeDash
              ? "bg-violet-500/10 font-medium text-violet-300"
              : "text-zinc-300 hover:bg-zinc-800/60"
          }`}
        >
          <button
            type="button"
            onClick={() => expansion.toggle(node.id)}
            title={open ? "Collapse" : "Expand"}
            className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-zinc-500 hover:text-zinc-200 focus-visible:outline-2 focus-visible:outline-violet-400"
          >
            <ChevronIcon open={open} />
          </button>
          <Link
            href={`/folders/${node.id}`}
            aria-current={activeDash ? "page" : undefined}
            onClick={onCloseDrawer}
            className="flex h-9 min-w-0 flex-1 items-center gap-1.5"
            title={`Open ${node.name} dashboard`}
          >
            <FolderIcon className="size-3.5 shrink-0 text-zinc-500" />
            <span className="min-w-0 flex-1 truncate">{node.name}</span>
          </Link>
          {collapsedCounts && <AttentionIndicator counts={collapsedCounts} />}
          <span className="hidden shrink-0 items-center gap-0.5 group-hover:flex group-focus-within:flex">
            <button
              type="button"
              title="New sub-department"
              onClick={() => editor.startCreate(node.id)}
              className="rounded p-1 text-zinc-500 hover:text-zinc-200"
            >
              <svg viewBox="0 0 16 16" fill="none" aria-hidden className="size-3">
                <path
                  d="M8 3v10M3 8h10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <button
              type="button"
              title="Rename"
              onClick={() => editor.startRename(node)}
              className="rounded p-1 text-zinc-500 hover:text-zinc-200"
            >
              <svg viewBox="0 0 16 16" fill="none" aria-hidden className="size-3">
                <path
                  d="m9.5 3.5 3 3L6 13H3v-3l6.5-6.5ZM8.25 4.75l3 3"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              title="Delete department"
              onClick={() => void editor.remove(node)}
              className="rounded p-1 text-zinc-500 hover:text-red-400"
            >
              <svg viewBox="0 0 16 16" fill="none" aria-hidden className="size-3">
                <path
                  d="M3.5 4.5h9m-7-1.5h5m-5.75 1.5.5 8h5.5l.5-8"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </span>
        </div>
      )}
      {open && (
        <>
          {editor.editing?.mode === "create" && editor.editing.parentId === node.id && (
            <FolderEditInput
              depth={depth + 1}
              value={editor.editValue}
              onChange={editor.setEditValue}
              onCommit={editor.commit}
              onCancel={editor.cancel}
            />
          )}
          {node.children.map((c) => (
            <FolderRow key={c.id} node={c} depth={depth + 1} scope={scope} />
          ))}
          {folderWorkers.map((w) => (
            <WorkerRow
              key={w.id}
              worker={w}
              depth={depth + 1}
              pathname={pathname}
              onCloseDrawer={onCloseDrawer}
              movement={movement}
            />
          ))}
        </>
      )}
    </div>
  );
}
